import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import moment from 'moment-timezone';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { SqlTypeCopySource } from 'app/model/pipeline/copysource/sql-type.copysource';
import { Owned } from 'app/model/base/named';
import { SqlPoolCopySource } from 'app/model/pipeline/copysource/sql-pool.copysource';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlPoolCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        sqlReaderQuery: '*',
        sqlReaderStoredProcedureName: '*',
        storedProcedureParameters: '*',
        queryTimeout: '*'
    }
})
export class SqlPoolCopySourceAdapter extends CopySourceAdapter<SqlPoolCopySource, Model.SqlPoolSource> {
    protected override async _toResource(model: SqlPoolCopySource): Promise<Model.SqlPoolSource> {
        const result: Model.SqlPoolSource = this.getCommonCopySourceProperties(
            model,
            adfModelTypeMap.sqlPool.copySourceType
        );
        if (model.mode === 'query') {
            result.sqlReaderQuery = ValueAdapter.toResource(model, SqlTypeCopySource.READER_QUERY);
        } else if (model.mode === 'storedProcedure') {
            result.sqlReaderStoredProcedureName = ValueAdapter.toResource(
                model,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME
            );
            if (model.readerStoredProcedureParameters != null && model.readerStoredProcedureParameters.length > 0) {
                const params: { [propertyName: string]: Model.StoredProcedureParameter } = {};
                model.readerStoredProcedureParameters.forEach((p) => {
                    params[p.name] = {
                        type: p.type,
                        value: ValueAdapter.toResource(p, ParameterConfig.VALUE)
                    };
                });
                result.storedProcedureParameters = params;
            } else {
                result.storedProcedureParameters = ValueAdapter.toResource(
                    model,
                    SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS
                );
            }
            // clear [] to keep the current behavior
            if (
                result.storedProcedureParameters &&
                Array.isArray(result.storedProcedureParameters) &&
                result.storedProcedureParameters.length === 0
            ) {
                result.storedProcedureParameters = undefined;
            }
        }

        try {
            result.queryTimeout =
                typeof model.queryTimeout === 'number'
                    ? moment.duration(model.queryTimeout, 'minutes').format('hh:mm:ss', {
                          trim: false
                      })
                    : ValueAdapter.toResource(model, SqlPoolCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert queryTimeout into TimeSpan');
        }

        return result;
    }

    protected override async _toModel(
        resource: Model.SqlPoolSource,
        copyActivity: CopyActivity
    ): Promise<SqlPoolCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            SqlPoolCopySource.MODEL,
            resource,
            copyActivity
        ) as SqlPoolCopySource;
        if (resource.sqlReaderQuery) {
            ValueAdapter.toModel(result, SqlTypeCopySource.READER_QUERY, resource.sqlReaderQuery);
            result.mode = 'query';
        } else if (resource.sqlReaderStoredProcedureName) {
            ValueAdapter.toModel(
                result,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME,
                resource.sqlReaderStoredProcedureName
            );
            if (resource.storedProcedureParameters != null) {
                if (
                    typeof resource.storedProcedureParameters === 'object' &&
                    resource.storedProcedureParameters.type === 'Expression'
                ) {
                    ValueAdapter.toModel(
                        result,
                        SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS,
                        resource.storedProcedureParameters
                    );
                } else {
                    result.readerStoredProcedureParameters = Object.keys(resource.storedProcedureParameters).map(
                        (key) => {
                            const item = Owned.create<ParameterConfig>(ParameterConfig.MODEL, copyActivity);
                            item.name = key;
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            item.type = resource.storedProcedureParameters![key].type;
                            ValueAdapter.toModel(
                                item,
                                ParameterConfig.VALUE,
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                resource.storedProcedureParameters![key].value
                            );
                            return item;
                        }
                    );
                }
            }
            result.mode = 'storedProcedure';
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                } else {
                    ValueAdapter.toModel(result, SqlPoolCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert queryTimeout into number');
        }

        return result;
    }
}
